import type { ChartBar } from "@/services/adapters/chart-adapter";

type Phase = "accumulation" | "distribution" | "neutral";

export interface WyckoffRow {
  time: string;
  phase: Phase;
  phase_name: string;
  strength: number;
  confidence: number;
  pos: number | null;
  atr: number | null;
  tr_high: number | null;
  tr_low: number | null;
  acc_score: number;
  dist_score: number;
  spring: boolean;
  upthrust: boolean;
  sos: boolean;
  sow: boolean;
}

export interface WyckoffSegment {
  phase: Phase;
  start_time: string;
  end_time: string;
  start_index: number;
  end_index: number;
  top: number;
  bottom: number;
  confidence: number;
  strength: number;
  bars: number;
}

export interface WyckoffSummary {
  phase: Phase;
  phase_name: string;
  strength: number;
  confidence: number;
  pos: number | null;
  atr_pct: number | null;
  event_counts: {
    spring: number;
    upthrust: number;
    sos: number;
    sow: number;
  };
}

export interface WyckoffEventItem {
  type: "spring" | "upthrust" | "sos" | "sow";
  label: string;
  time: string;
  close: number;
  phase: Phase;
}

export interface WyckoffMarker {
  time: string;
  position: "aboveBar" | "belowBar";
  color: string;
  shape: "circle" | "arrowUp" | "arrowDown";
  text: string;
}

export interface LocalWyckoffAnalysis {
  rows: WyckoffRow[];
  segments: WyckoffSegment[];
  summary: WyckoffSummary;
  events: WyckoffEventItem[];
  markers: WyckoffMarker[];
}

const PHASE_NAME_MAP: Record<Phase, string> = {
  accumulation: "吸筹",
  distribution: "派发",
  neutral: "中性",
};

function clamp(value: number, min: number, max: number) {
  return Math.max(min, Math.min(max, value));
}

function rollingMean(values: number[], window: number) {
  const out = new Array<number | null>(values.length).fill(null);
  if (window <= 0) {
    return out;
  }
  let sum = 0;
  let valid = 0;
  for (let i = 0; i < values.length; i += 1) {
    const incoming = Number.isFinite(values[i]) ? values[i] : NaN;
    if (Number.isFinite(incoming)) {
      sum += incoming;
      valid += 1;
    }
    if (i >= window) {
      const leaving = values[i - window];
      if (Number.isFinite(leaving)) {
        sum -= leaving;
        valid -= 1;
      }
    }
    if (i >= window - 1 && valid > 0) {
      out[i] = sum / valid;
    }
  }
  return out;
}

function rollingMax(values: number[], window: number) {
  const out = new Array<number | null>(values.length).fill(null);
  for (let i = window - 1; i < values.length; i += 1) {
    let max = -Infinity;
    for (let j = i - window + 1; j <= i; j += 1) {
      max = Math.max(max, values[j]);
    }
    out[i] = Number.isFinite(max) ? max : null;
  }
  return out;
}

function rollingMin(values: number[], window: number) {
  const out = new Array<number | null>(values.length).fill(null);
  for (let i = window - 1; i < values.length; i += 1) {
    let min = Infinity;
    for (let j = i - window + 1; j <= i; j += 1) {
      min = Math.min(min, values[j]);
    }
    out[i] = Number.isFinite(min) ? min : null;
  }
  return out;
}

function forwardMax(values: number[], window: number) {
  const out = new Array<number | null>(values.length).fill(null);
  for (let i = 0; i < values.length; i += 1) {
    let max = -Infinity;
    for (let j = i + 1; j <= i + window && j < values.length; j += 1) {
      max = Math.max(max, values[j]);
    }
    out[i] = max === -Infinity ? null : max;
  }
  return out;
}

function forwardMin(values: number[], window: number) {
  const out = new Array<number | null>(values.length).fill(null);
  for (let i = 0; i < values.length; i += 1) {
    let min = Infinity;
    for (let j = i + 1; j <= i + window && j < values.length; j += 1) {
      min = Math.min(min, values[j]);
    }
    out[i] = min === Infinity ? null : min;
  }
  return out;
}

function buildSegments(rows: WyckoffRow[]): WyckoffSegment[] {
  if (!rows.length) {
    return [];
  }
  const segments: WyckoffSegment[] = [];
  let start = 0;
  let current = rows[0].phase;
  for (let i = 1; i <= rows.length; i += 1) {
    const ended = i === rows.length || rows[i].phase !== current;
    if (!ended) {
      continue;
    }
    const chunk = rows.slice(start, i);
    const highs = chunk.map((row) => row.tr_high).filter((value): value is number => Number.isFinite(value));
    const lows = chunk.map((row) => row.tr_low).filter((value): value is number => Number.isFinite(value));
    if (highs.length && lows.length) {
      const strength = chunk.reduce((acc, row) => acc + row.strength, 0) / chunk.length;
      const confidence = chunk.reduce((acc, row) => acc + row.confidence, 0) / chunk.length;
      segments.push({
        phase: current,
        start_time: chunk[0].time,
        end_time: chunk[chunk.length - 1].time,
        start_index: start,
        end_index: i - 1,
        top: Math.max(...highs),
        bottom: Math.min(...lows),
        confidence: Number(confidence.toFixed(2)),
        strength: Number(strength.toFixed(2)),
        bars: chunk.length,
      });
    }
    start = i;
    if (i < rows.length) {
      current = rows[i].phase;
    }
  }
  return segments;
}

export function analyzeWyckoffLocal(bars: ChartBar[]): LocalWyckoffAnalysis {
  if (bars.length < 60) {
    return {
      rows: [],
      segments: [],
      summary: {
        phase: "neutral",
        phase_name: "数据不足",
        strength: 0,
        confidence: 0,
        pos: null,
        atr_pct: null,
        event_counts: { spring: 0, upthrust: 0, sos: 0, sow: 0 },
      },
      events: [],
      markers: [],
    };
  }

  const high = bars.map((bar) => bar.high);
  const low = bars.map((bar) => bar.low);
  const close = bars.map((bar) => bar.close);
  const volume = bars.map((bar) => bar.volume);
  const prevClose = close.map((_, index) => (index > 0 ? close[index - 1] : NaN));
  const tr = bars.map((_, index) => {
    if (index === 0 || !Number.isFinite(prevClose[index])) {
      return high[index] - low[index];
    }
    return Math.max(
      high[index] - low[index],
      Math.abs(high[index] - prevClose[index]),
      Math.abs(low[index] - prevClose[index]),
    );
  });

  const atr = rollingMean(tr, 14);
  const trHigh = rollingMax(high, 100);
  const trLow = rollingMin(low, 100);
  const trRange = trHigh.map((value, index) =>
    Number.isFinite(value) && Number.isFinite(trLow[index]) ? Math.max(0, (value as number) - (trLow[index] as number)) : null,
  );
  const pos = close.map((value, index) => {
    if (!Number.isFinite(trRange[index]) || (trRange[index] as number) <= 0) {
      return null;
    }
    return clamp((value - (trLow[index] as number)) / (trRange[index] as number), 0, 1);
  });

  const ret = close.map((value, index) => (index > 0 && prevClose[index] > 0 ? value / prevClose[index] - 1 : NaN));
  const ret5 = rollingMean(ret, 5);
  const spread = high.map((value, index) => Math.max(0.0000001, value - low[index]));
  const vma20 = rollingMean(volume, 20);
  const vRel = volume.map((value, index) =>
    Number.isFinite(vma20[index]) && (vma20[index] as number) > 0 ? value / (vma20[index] as number) : NaN,
  );
  const clv = bars.map((bar) => {
    const denom = bar.high - bar.low;
    if (!Number.isFinite(denom) || denom <= 0) {
      return NaN;
    }
    return clamp((bar.close - bar.low) / denom, 0, 1);
  });
  const spreadOverAtr = spread.map((value, index) =>
    Number.isFinite(atr[index]) && (atr[index] as number) > 0 ? value / (atr[index] as number) : NaN,
  );
  const effort = vRel.map((value, index) =>
    Number.isFinite(value) && Number.isFinite(spreadOverAtr[index]) && spreadOverAtr[index] > 0
      ? value / spreadOverAtr[index]
      : NaN,
  );
  const absorb = rollingMean(effort, 10);
  const absorbBase = rollingMean(absorb.map((value) => (value != null && Number.isFinite(value) ? value : Number.NaN)), 60);
  const absorbNorm = absorb.map((value, index) => {
    const current = value != null ? Number(value) : Number.NaN;
    const base = absorbBase[index] != null ? Number(absorbBase[index]) : Number.NaN;
    if (!Number.isFinite(current) || !Number.isFinite(base) || base <= 0) {
      return 0;
    }
    return clamp(current / base, 0, 3) / 3;
  });

  const fwdMax = forwardMax(close, 3);
  const fwdMin = forwardMin(close, 3);

  const spring = bars.map((_, index) => {
    if (!Number.isFinite(trLow[index]) || !Number.isFinite(atr[index]) || (atr[index] as number) <= 0) {
      return false;
    }
    const depth = (trLow[index] as number) - low[index];
    return (
      depth > 0.35 * (atr[index] as number) &&
      close[index] > (trLow[index] as number) &&
      clv[index] > 0.58 &&
      Number.isFinite(fwdMax[index]) &&
      (fwdMax[index] as number) > close[index]
    );
  });

  const upthrust = bars.map((_, index) => {
    if (!Number.isFinite(trHigh[index]) || !Number.isFinite(atr[index]) || (atr[index] as number) <= 0) {
      return false;
    }
    const height = high[index] - (trHigh[index] as number);
    return (
      height > 0.35 * (atr[index] as number) &&
      close[index] < (trHigh[index] as number) &&
      clv[index] < 0.42 &&
      Number.isFinite(fwdMin[index]) &&
      (fwdMin[index] as number) < close[index]
    );
  });

  const sos = bars.map(
    (_, index) =>
      Number.isFinite(pos[index]) &&
      (pos[index] as number) > 0.56 &&
      Number.isFinite(ret5[index]) &&
      (ret5[index] as number) > 0 &&
      Number.isFinite(clv[index]) &&
      clv[index] > 0.55 &&
      Number.isFinite(vRel[index]) &&
      (vRel[index] as number) > 1.02,
  );
  const sow = bars.map(
    (_, index) =>
      Number.isFinite(pos[index]) &&
      (pos[index] as number) < 0.44 &&
      Number.isFinite(ret5[index]) &&
      (ret5[index] as number) < 0 &&
      Number.isFinite(clv[index]) &&
      clv[index] < 0.45 &&
      ((Number.isFinite(vRel[index]) && (vRel[index] as number) > 1.02) ||
        (Number.isFinite(spreadOverAtr[index]) && (spreadOverAtr[index] as number) > 0.85)),
  );

  const rows: WyckoffRow[] = bars.map((bar, index) => {
    const accScore = clamp(
      ((0.5 - (pos[index] ?? 0.5)) * 2 > 0 ? (0.5 - (pos[index] ?? 0.5)) * 2 : 0) +
        absorbNorm[index] +
        (spring[index] ? 1.0 : 0) +
        (sos[index] ? 0.5 : 0),
      0,
      5,
    );
    const distScore = clamp(
      (((pos[index] ?? 0.5) - 0.5) * 2 > 0 ? ((pos[index] ?? 0.5) - 0.5) * 2 : 0) +
        absorbNorm[index] +
        (upthrust[index] ? 1.0 : 0) +
        (sow[index] ? 0.5 : 0),
      0,
      5,
    );
    let phase: Phase = "neutral";
    if (accScore > distScore + 0.35) {
      phase = "accumulation";
    } else if (distScore > accScore + 0.35) {
      phase = "distribution";
    }
    const dominant = Math.max(accScore, distScore);
    const confidence = clamp((Math.abs(accScore - distScore) / 2.2) * 100, 0, 100);
    const strength = clamp((dominant / 4) * 100, 0, 100);
    return {
      time: bar.time,
      phase,
      phase_name: PHASE_NAME_MAP[phase],
      strength: Number(strength.toFixed(2)),
      confidence: Number(confidence.toFixed(2)),
      pos: pos[index],
      atr: atr[index],
      tr_high: trHigh[index],
      tr_low: trLow[index],
      acc_score: Number(accScore.toFixed(4)),
      dist_score: Number(distScore.toFixed(4)),
      spring: spring[index],
      upthrust: upthrust[index],
      sos: sos[index],
      sow: sow[index],
    };
  });

  const events: WyckoffEventItem[] = [];
  const markers: WyckoffMarker[] = [];
  for (let i = rows.length - 1; i >= 0; i -= 1) {
    const row = rows[i];
    if (row.spring) {
      events.push({ type: "spring", label: "SPRING", time: row.time, close: bars[i].close, phase: row.phase });
      markers.push({ time: row.time, position: "belowBar", color: "#0f9d7a", shape: "arrowUp", text: "SPR" });
    }
    if (row.sos) {
      events.push({ type: "sos", label: "SOS", time: row.time, close: bars[i].close, phase: row.phase });
      markers.push({ time: row.time, position: "belowBar", color: "#1f8ef1", shape: "circle", text: "SOS" });
    }
    if (row.upthrust) {
      events.push({ type: "upthrust", label: "UPTHRUST", time: row.time, close: bars[i].close, phase: row.phase });
      markers.push({ time: row.time, position: "aboveBar", color: "#dd5b42", shape: "arrowDown", text: "UT" });
    }
    if (row.sow) {
      events.push({ type: "sow", label: "SOW", time: row.time, close: bars[i].close, phase: row.phase });
      markers.push({ time: row.time, position: "aboveBar", color: "#b45309", shape: "circle", text: "SOW" });
    }
  }

  const summaryRow = rows[rows.length - 1];
  const recent = rows.slice(-20);
  const atrPct =
    Number.isFinite(summaryRow.atr) && bars[bars.length - 1].close > 0
      ? Number((((summaryRow.atr as number) / bars[bars.length - 1].close) * 100).toFixed(4))
      : null;

  return {
    rows,
    segments: buildSegments(rows),
    summary: {
      phase: summaryRow.phase,
      phase_name: summaryRow.phase_name,
      strength: summaryRow.strength,
      confidence: summaryRow.confidence,
      pos: summaryRow.pos,
      atr_pct: atrPct,
      event_counts: {
        spring: recent.filter((row) => row.spring).length,
        upthrust: recent.filter((row) => row.upthrust).length,
        sos: recent.filter((row) => row.sos).length,
        sow: recent.filter((row) => row.sow).length,
      },
    },
    events,
    markers,
  };
}
